import React, { useId, useRef, useState, useLayoutEffect } from "react"

/**
 * Sparkline — tiny inline trend chart (pure SVG, no deps).
 * Uses a pixel-matched viewBox (measured container width) so the end dot stays a
 * perfect circle instead of being stretched by non-uniform scaling.
 *
 * @param {number[]} values - series values (oldest → newest)
 * @param {string} color - stroke/fill accent (any CSS color, incl. var(--...))
 * @param {number} height - px height (default 44)
 * @param {number} strokeWidth
 */
const Sparkline = ({ values = [], color = 'currentColor', height = 44, strokeWidth = 2 }) => {
  const gid = useId()
  const ref = useRef(null)
  const [w, setW] = useState(0)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setW(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const clean = (values || []).filter((v) => typeof v === 'number' && !isNaN(v))
  const H = height
  const pad = strokeWidth + 2
  const ready = w > 4 && clean.length > 1

  let d = '', area = '', ex = 0, ey = 0
  if (ready) {
    const min = Math.min(...clean)
    const max = Math.max(...clean)
    const span = max - min || 1
    const stepX = w / (clean.length - 1)
    const pts = clean.map((v, i) => [i * stepX, pad + (H - pad * 2) * (1 - (v - min) / span)])
    d = `M ${pts[0][0]},${pts[0][1]}`
    for (let i = 1; i < pts.length; i++) {
      const [x0, y0] = pts[i - 1]
      const [x1, y1] = pts[i]
      const mx = (x0 + x1) / 2
      d += ` Q ${x0},${y0} ${mx},${(y0 + y1) / 2} T ${x1},${y1}`
    }
    ;[ex, ey] = pts[pts.length - 1]
    area = `${d} L ${ex},${H} L ${pts[0][0]},${H} Z`
  }

  return (
    <div ref={ref} style={{ width: '100%', height: H }}>
      {ready && (
        <svg width={w} height={H} viewBox={`0 0 ${w} ${H}`} style={{ display: 'block', overflow: 'visible' }} aria-hidden>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.26" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${gid})`} stroke="none" />
          <path d={d} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={ex} cy={ey} r={strokeWidth + 1} fill={color} />
        </svg>
      )}
    </div>
  )
}

export default Sparkline
